import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import moment from 'moment';

import {
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  ModalFooterAlignment,
} from 'design-system/src/components/Modal.tsx';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow from 'design-system/src/components/ButtonRow/ButtonRow.tsx';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import Hairline from 'design-system/src/components/Hairline/Hairline.tsx';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox.tsx';

import { createTrackingProps } from 'shared-utils/analytics';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { Dropdown, DropdownItem, DropdownContainer } from '../../Dropdown.tsx';
import { EnrollmentType } from '../../../types.ts';
import { DropdownOption } from 'shared-utils/types/dropdownFilter';
import { Patient, client } from '../../../api.tsx';
import { FormLabel } from '../../FormLabel.tsx';
import Typeahead, { TypeaheadOption } from 'design-system/src/components/Typeahead/Typeahead';
import { Input, DateInput } from '../../Input.tsx';
import { validateNonWhitespaceField } from '../../../utils.tsx';

const SpacedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledInfoBox = styled(InfoBox)`
  gap: 4px;

  > svg {
    margin-top: 2px;
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  // TODO: use MODAL_INNER_GAP when it's available
  gap: 16px;
`;

const StyledHairline = styled(Hairline)`
  // the flex gap is 12px and we want the hairline to have 24px
  margin: 12px 0;
`;

type FormData = {
  drug_name: string;
  enrollment_type: EnrollmentType;
  template_name: string;
  patient_id: string;
  // if choosing to add a new patient
  patient_first_name: string;
  patient_last_name: string;
  patient_dob: string;
};

export type NewEnrollmentModalProps = {
  onClose: () => void;
};

type DrugOption = {
  value: string;
  description: string;
};

type TemplateOption = {
  value: string;
  description: string;
};

type EnrollmentTemplate = {
  id: string;
  name: string;
};

type TemplatesData = {
  templates: Record<string, Record<string, EnrollmentTemplate[]>>;
};

/**
 * Modal for initiating a new enrollment for a patient (existing or new)
 */
export const NewEnrollmentModal = ({ onClose }: NewEnrollmentModalProps) => {
  const [step, setStep] = useState<'basicInfo' | 'patientInfo'>('basicInfo');

  // TODO: put loading spinner in list option if it's pending
  const [patientOptions, setPatientOptions] = useState<TypeaheadOption[]>([]);
  // TODO: put loading spinner in list option if it's pending
  const [drugOptions, setDrugOptions] = useState<DrugOption[]>([]);
  const [enrollmentOptions, setEnrollmentOptions] = useState<Record<string, DropdownOption[]>>({});
  const [templateOptions, setTemplateOptions] = useState<Record<string, Record<string, TemplateOption[]>>>({});

  // setting state for each form field instead of using form state to avoid an issue where the form state
  // for the basic info step is being reused for the patient info step
  const [drug, setDrug] = useState<string>('');
  const [enrollmentType, setEnrollmentType] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('');
  const [patientId, setPatientId] = useState<string>('');
  const [patientFirstName, setPatientFirstName] = useState<string>('');
  const [patientLastName, setPatientLastName] = useState<string>('');
  const [patientDob, setPatientDob] = useState<string>('');

  const navigate = useNavigate();

  const drugLabelId = useId();
  const enrollmentTypeLabelId = useId();
  const templateNameLabelId = useId();
  const patientLabelId = useId();

  const { control, formState } = useForm<FormData>({
    defaultValues: {
      patient_id: '',
      drug_name: '',
      enrollment_type: '' as EnrollmentType,
      template_name: '',
      patient_first_name: '',
      patient_last_name: '',
      patient_dob: '',
    },
    mode: 'onChange',
  });

  const primaryButtonIsDisabled = () => {
    if (step === 'basicInfo') {
      return !drug || !enrollmentType || !templateName;
    }

    return !formState.isValid;
  };

  useQuery({
    queryKey: ['patients'],
    queryFn: () => {
      return client.get('patients').then((res) => {
        if (res.data.patients) {
          const patientOptions = res.data.patients.map((patient: Patient) => createPatientOption(patient));
          patientOptions.unshift({ value: 'new', description: '+ Add new patient' });
          setPatientOptions(patientOptions);
        }

        return res.data;
      });
    },
  });

  const templatesQuery = useQuery<TemplatesData>({
    queryKey: ['enrollments-templates'],
    queryFn: () => {
      return client.get('/enrollments/templates').then((res) => res.data);
    },
  });

  useEffect(() => {
    if (templatesQuery.data) {
      const drugNames = Object.keys(templatesQuery.data.templates);

      const newDrugOptions: DrugOption[] = drugNames.map((drugName) => ({
        value: drugName,
        description: drugName,
      }));
      setDrugOptions(newDrugOptions);

      const newEnrollmentOptions: Record<string, DropdownOption[]> = {};
      const newTemplateOptions: Record<string, Record<string, TemplateOption[]>> = {};

      drugNames.forEach((drugName) => {
        const enrollmentTypes = Object.keys(templatesQuery.data.templates[drugName]);
        newEnrollmentOptions[drugName] = enrollmentTypes.map((enrollmentType) => ({
          value: enrollmentType,
          description: EnrollmentType[enrollmentType as keyof typeof EnrollmentType],
        }));

        newTemplateOptions[drugName] = {};
        enrollmentTypes.forEach((enrollmentType) => {
          newTemplateOptions[drugName][enrollmentType] = templatesQuery.data.templates[drugName][enrollmentType].map(
            (template: EnrollmentTemplate) => ({
              value: template.name,
              description: template.name,
            }),
          );
        });
      });

      setEnrollmentOptions(newEnrollmentOptions);
      setTemplateOptions(newTemplateOptions);
    }
  }, [templatesQuery.data]);

  const createPatientOption = (patient: Patient): TypeaheadOption => {
    const option: TypeaheadOption = {
      value: patient.id,
      description: `${patient.first_name} ${patient.last_name} (${formatStandardDate(patient.date_of_birth)})`,
    };
    return option;
  };

  const submit = useMutation<{ pending_enrollment_id: string }, AxiosError<{ error: string }>>({
    mutationFn: async () => {
      const data = {
        patient_id: patientId === 'new' ? null : patientId,
        drug_name: drug,
        enrollment_type: enrollmentType,
        template_name: templateName,
        patient_first_name: patientFirstName.trim(),
        patient_last_name: patientLastName.trim(),
        patient_dob: patientDob,
      };

      return client.post(`enrollments/create-form`, data).then((res) => res.data);
    },
    onSuccess: ({ pending_enrollment_id }) => {
      navigate(`/enrollments/pending/${pending_enrollment_id}`);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      Sentry.captureException(error);
      console.error(error);
    },
  });

  const basicInfoForm = (
    <>
      <ModalTitle>Enroll patient in manufacturer program</ModalTitle>
      <ModalBody>
        <SpacedContent {...createTrackingProps({ unmask: true })}>
          <Typography styledAs="bodyLargeDMSans">
            Manufacturer programs help patients access or afford their medication. Collect the patient's signature in
            office to start their enrollment. We'll send you a task to finalize and submit after you prescribe.
          </Typography>
          <StyledInfoBox variant="surface" noTopMargin noBottomMargin>
            <Typography styledAs="bodySmallDMSans" renderedAs="div">
              This is for new prescriptions only. For existing prescriptions,{' '}
              <Button variant="link" to="/tasks/new?source=portal_new_enrollment_modal">
                create a task
              </Button>{' '}
              instead.
            </Typography>
          </StyledInfoBox>
        </SpacedContent>
        <FormContainer>
          <DropdownContainer>
            <FormLabel id={drugLabelId}>Drug</FormLabel>
            <Typeahead
              labelId={drugLabelId}
              options={drugOptions}
              onChange={(option) => {
                setDrug(option);
              }}
              value={drug}
              {...createTrackingProps({ elementName: 'New In-Office Enrollment: Drug Dropdown', unmask: true })}
            />
          </DropdownContainer>

          {drug && enrollmentOptions[drug] && enrollmentOptions[drug].length > 0 && (
            <Controller
              name="enrollment_type"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <DropdownContainer>
                  <FormLabel id={enrollmentTypeLabelId}>Enrollment type</FormLabel>
                  <Dropdown
                    labelId={enrollmentTypeLabelId}
                    onChange={(event) => {
                      setEnrollmentType(event.target.value as string);
                      field.onChange(event.target.value);
                    }}
                    defaultValue=""
                    value={enrollmentType}
                    {...createTrackingProps({
                      elementName: 'New In-Office Enrollment: Type Dropdown',
                      unmask: true,
                    })}
                  >
                    {enrollmentOptions[drug].map((option) => (
                      <DropdownItem key={option.value} value={option.value}>
                        {option.description}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                </DropdownContainer>
              )}
            />
          )}
          {drug &&
            enrollmentType &&
            templateOptions[drug][enrollmentType] &&
            templateOptions[drug][enrollmentType].length > 0 && (
              <Controller
                name="template_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DropdownContainer>
                    <FormLabel id={templateNameLabelId}>Enrollment form</FormLabel>
                    <Dropdown
                      labelId={templateNameLabelId}
                      onChange={(event) => {
                        setTemplateName(event.target.value as string);
                        field.onChange(event.target.value);
                      }}
                      value={templateName}
                      defaultValue=""
                      {...createTrackingProps({
                        elementName: 'New In-Office Enrollment: Form Dropdown',
                        unmask: true,
                      })}
                    >
                      {templateOptions[drug][enrollmentType].map((option) => (
                        <DropdownItem key={option.value} value={option.value}>
                          {option.description}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  </DropdownContainer>
                )}
              />
            )}
        </FormContainer>
      </ModalBody>
    </>
  );

  const patientInfoForm = (
    <>
      <ModalTitle>Select or add a patient</ModalTitle>
      <ModalBody>
        <Typography styledAs="bodyLargeDMSans">
          Please select or add the patient this enrollment form is for.
        </Typography>
        <FormContainer>
          <Controller
            name="patient_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DropdownContainer>
                <FormLabel id={patientLabelId}>Patient</FormLabel>
                <Typeahead
                  labelId={patientLabelId}
                  value={patientId}
                  options={patientOptions}
                  onChange={(option) => {
                    setPatientId(option);
                    field.onChange(option);
                    // clear out the new patient info fields in case the user was creating a new patient but switched to an existing patient
                    setPatientFirstName('');
                    setPatientLastName('');
                    setPatientDob('');
                  }}
                  // Track element but DO NOT unmask - has patient info
                  {...createTrackingProps({ elementName: 'New In-Office Enrollment Modal: Patient Dropdown' })}
                />
              </DropdownContainer>
            )}
          />
          {patientId === 'new' && (
            // Track element but DO NOT unmask - has patient info
            <div
              {...createTrackingProps({ elementName: 'New In-Office Enrollment: New Patient Form' })}
              className="flex flex-col gap-3"
            >
              <StyledHairline />
              <Controller
                control={control}
                name="patient_first_name"
                rules={{
                  required: true,
                  validate: validateNonWhitespaceField('First name'),
                }}
                render={({ field }) => (
                  <Input
                    label="First name"
                    placeholder="Patient's first name"
                    {...field}
                    value={patientFirstName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setPatientFirstName(event.target.value as string);
                      field.onChange(event.target.value);
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="patient_last_name"
                rules={{
                  required: true,
                  validate: validateNonWhitespaceField('Last name'),
                }}
                render={({ field }) => (
                  <Input
                    label="Last name"
                    placeholder="Patient's last name"
                    {...field}
                    value={patientLastName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setPatientLastName(event.target.value as string);
                      field.onChange(event.target.value);
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="patient_dob"
                rules={{ required: true }}
                render={({ field }) => (
                  <DateInput
                    label="Date of birth"
                    value={patientDob ? moment(patientDob) : null}
                    onChange={(value) => {
                      setPatientDob(value ? value.toISOString() : '');
                      field.onChange(value ? value.toISOString() : '');
                    }}
                  />
                )}
              />
            </div>
          )}
        </FormContainer>
      </ModalBody>
    </>
  );

  const buttonAnalyticsName =
    step === 'basicInfo'
      ? 'New In-Office Enrollment: Select Form Button'
      : 'New In-Office Enrollment: Create Form Button';
  return (
    <Modal onClose={onClose}>
      {step === 'basicInfo' ? basicInfoForm : patientInfoForm}

      <ModalFooter align={ModalFooterAlignment.SpaceBetween}>
        <div>
          {step === 'patientInfo' && (
            <Button variant="tertiary" onClick={() => setStep('basicInfo')} disabled={submit.isPending}>
              Back
            </Button>
          )}
        </div>

        <ButtonRow>
          <Button
            variant="tertiary"
            onClick={onClose}
            disabled={submit.isPending}
            {...createTrackingProps({ elementName: 'New In-Office Enrollment: Cancel Button' })}
          >
            Cancel
          </Button>

          <Button
            isLoading={submit.isPending}
            disabled={primaryButtonIsDisabled()}
            disabledButtonTooltipContent="Please complete all fields to continue."
            onClick={() => {
              if (step === 'basicInfo') {
                setStep('patientInfo');
              } else {
                submit.mutate();
              }
            }}
            {...createTrackingProps({ elementName: buttonAnalyticsName })}
          >
            {step === 'basicInfo' ? 'Continue' : 'Create'}
          </Button>
        </ButtonRow>
      </ModalFooter>
    </Modal>
  );
};

export default NewEnrollmentModal;
